import api from "@/lib/api";

// Harvest lots weighed at a farm with no network are kept on this device
// and uploaded once it's back online. Each lot carries a client_ref the
// server dedupes on, so re-uploading after a dropped connection (where
// the server may have saved it but the reply never arrived) can't record
// the same lot twice.
const QUEUE_KEY = "agri_pending_harvests";
const SYNC_INTERVAL_MS = 30000;
export const PENDING_EVENT = "agri-pending-changed";

// Lots are tagged with the tenant they were weighed for, and only ever
// uploaded while logged in to that same tenant (a shared phone may log in
// to a different business before the lots get a chance to upload).
let currentTenant = null;
export const setSyncTenant = (slug) => { currentTenant = slug || null; };

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeQueue(q) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  window.dispatchEvent(new Event(PENDING_EVENT));
}

export function newClientRef() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // crypto.randomUUID needs https; plain-http LAN testing falls back here.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

// No response at all (offline, DNS failure, timeout) -- as opposed to the
// server answering with an error, which re-sending won't fix.
export const isNetworkError = (err) => !!err && !err.response;

export function pendingHarvests(farmerId) {
  return readQueue().filter((p) => p.tenant === currentTenant && (!farmerId || p.farmer_id === farmerId));
}

export function queueHarvest({ farmerId, farmerName, payload, summary }) {
  // Throws if storage is unavailable -- the caller must not report the lot as saved then.
  writeQueue([...readQueue(), {
    client_ref: payload.client_ref, tenant: currentTenant, farmer_id: farmerId,
    farmer_name: farmerName, payload, summary, queued_at: new Date().toISOString(), error: null,
  }]);
}

export function discardPendingHarvest(clientRef) {
  writeQueue(readQueue().filter((p) => p.client_ref !== clientRef));
}

let syncing = null;

// Uploads this tenant's waiting lots, oldest first. Stops at the first
// network failure (still offline -- try again later). A lot the server
// rejects outright (e.g. the farmer was deleted meanwhile) stays on the
// device with its error shown, for the user to look at and discard.
export function syncPendingHarvests() {
  if (syncing) return syncing;
  syncing = (async () => {
    let synced = 0;
    for (const item of pendingHarvests()) {
      try {
        await api.post(`/farmers/${item.farmer_id}/harvests`, item.payload);
        writeQueue(readQueue().filter((p) => p.client_ref !== item.client_ref));
        synced += 1;
      } catch (err) {
        if (isNetworkError(err) || err.response.status === 401) break;
        const detail = err.response.data?.detail;
        writeQueue(readQueue().map((p) => (p.client_ref === item.client_ref
          ? { ...p, error: typeof detail === "string" ? detail : `Server error ${err.response.status}` }
          : p)));
      }
    }
    return { synced, remaining: pendingHarvests().length };
  })().finally(() => { syncing = null; });
  return syncing;
}

// Retries on reconnect, and every 30s while anything is waiting (the
// "online" event alone is unreliable on weak mobile signal). onSynced is
// called with the number uploaded whenever a pass uploads something.
export function startAutoSync(onSynced) {
  const run = async () => {
    if (navigator.onLine === false || pendingHarvests().length === 0) return;
    const { synced } = await syncPendingHarvests();
    if (synced > 0) onSynced?.(synced);
  };
  run();
  window.addEventListener("online", run);
  const timer = setInterval(run, SYNC_INTERVAL_MS);
  return () => {
    window.removeEventListener("online", run);
    clearInterval(timer);
  };
}
